import React from 'react';
import {
  EditorConfig,
  LexicalEditor,
  DOMExportOutput,
  SerializedTextNode,
  DOMConversionOutput,
  DOMConversionMap,
  LexicalNode,
  NodeKey,
  isHTMLElement,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  COMMAND_PRIORITY_LOW,
  TextNode,
  TextModeType,
  DOMConversion,
} from 'lexical';
import { createCommand } from 'lexical';
import { mergeRegister } from '@lexical/utils';

export const CROSS_REFERENCE_MODAL_COMMAND = createCommand<{ nodeKey: string }>();

export type PhxCrossReferenceLinkAttributes = {
  id: string;
  contentUri: string;
  title: string;
};

export type SerializedPhxCrossReferenceLinkNode = SerializedTextNode & {
  type: 'phx-cross-ref-link';
  version: 1;
  id: string;
  contentUri: string;
  title: string;
};

export class PhxCrossReferenceLinkNode extends TextNode {
  static _editor: LexicalEditor | null = null;

  static setEditor(editor: LexicalEditor) {
    PhxCrossReferenceLinkNode._editor = editor;
  }

  __id: string;
  __contentUri: string;
  private __clickHandler: (e: MouseEvent) => void;

  static getType(): string {
    return 'phx-cross-ref-link';
  }

  static clone(node: PhxCrossReferenceLinkNode): PhxCrossReferenceLinkNode {
    return new PhxCrossReferenceLinkNode(
      node.__text,
      {
        id: node.__id,
        contentUri: node.__contentUri,
        title: node.__text,
      },
      node.getKey()
    );
  }

  constructor(text: string, attributes: PhxCrossReferenceLinkAttributes, key?: NodeKey) {
    super(text, key);
    const { id, contentUri } = attributes;
    this.__id = id;
    this.__contentUri = contentUri;
    this.__clickHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (PhxCrossReferenceLinkNode._editor) {
        PhxCrossReferenceLinkNode._editor.dispatchCommand(CROSS_REFERENCE_MODAL_COMMAND, {
          nodeKey: this.getKey(),
        });
      }
    };
  }

  static registerCommands(editor: LexicalEditor, nodeKey: NodeKey, isSelected: boolean): () => void {
    return mergeRegister(
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if (selection && $isNodeSelection(selection)) {
            const nodes = selection.getNodes();
            if (nodes.some((node) => node.getKey() === nodeKey)) {
              event.preventDefault();
              editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if (node instanceof PhxCrossReferenceLinkNode) {
                  node.remove();
                }
              });
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if (selection && $isNodeSelection(selection)) {
            const nodes = selection.getNodes();
            if (nodes.some((node) => node.getKey() === nodeKey)) {
              event.preventDefault();
              editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if (node instanceof PhxCrossReferenceLinkNode) {
                  node.remove();
                }
              });
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('a');
    element.className = 'phx-cross-ref-link';
    element.href = this.__contentUri;
    element.id = this.__id;
    element.textContent = this.__text;
    element.addEventListener('click', this.__clickHandler);
    return element;
  }

  updateDOM(prevNode: this, anchor: HTMLElement, config: EditorConfig): boolean {
    const contentUri = this.__contentUri;
    const id = this.__id;
    const text = this.__text;

    if (contentUri !== prevNode.__contentUri) {
      (anchor as HTMLAnchorElement).href = contentUri;
    }

    if (id !== prevNode.__id) {
      anchor.id = id;
    }

    if (text !== prevNode.__text) {
      anchor.textContent = text;
    }

    // Remove old click handler and add the new one
    anchor.removeEventListener('click', prevNode.__clickHandler);
    anchor.addEventListener('click', this.__clickHandler);

    return false;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('a');
    element.className = 'phx-cross-ref-link';
    element.href = this.__contentUri;
    element.id = this.__id;
    element.textContent = this.__text;
    return { element };
  }

  exportJSON(): SerializedPhxCrossReferenceLinkNode {
    return {
      ...super.exportJSON(),
      type: 'phx-cross-ref-link',
      version: 1,
      id: this.__id,
      contentUri: this.__contentUri,
      title: this.__text,
    };
  }

  static importJSON(serializedNode: SerializedPhxCrossReferenceLinkNode): PhxCrossReferenceLinkNode {
    const { id, contentUri, text } = serializedNode;
    return new PhxCrossReferenceLinkNode(text, { id, contentUri, title: text });
  }

  static importDOM(): DOMConversionMap {
    return {
      a: (domNode: HTMLElement): DOMConversion<HTMLElement> | null => {
        if (!domNode.classList.contains('phx-cross-ref-link')) {
          return null;
        }
        return {
          conversion: () => {
            const contentUri = domNode.getAttribute('href') || '';
            const id = domNode.getAttribute('id') || '';
            const text = domNode.getAttribute('title') || domNode.textContent || '';
            return {
              node: new PhxCrossReferenceLinkNode(text, { id, contentUri, title: text }),
            };
          },
          priority: 1,
        };
      },
    };
  }
}

export function $isPhxCrossReferenceLinkNode(node: LexicalNode | null | undefined): node is PhxCrossReferenceLinkNode {
  return !!node && node.getType() === 'phx-cross-ref-link';
}

export function $createPhxCrossReferenceLinkNode(
  id: string,
  contentUri: string,
  title: string
): PhxCrossReferenceLinkNode {
  return new PhxCrossReferenceLinkNode(title, { id, contentUri, title });
}
